import React, { useEffect, useMemo, useRef, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { DownloadIcon, TrashIcon, EditIcon } from '../assets/icons/Icons';
import '../styles/journal.css';

const STORAGE_KEY = 'ishira.journal.entries.v1';

const PROMPTS = [
  'What is one thing you noticed about your mood today?',
  'What drained your energy, and what restored it?',
  'Name three things you’re grateful for, however small.',
  'What is a worry you can let go of for tonight?',
  'Describe a moment today when you felt most like yourself.',
];

const loadEntries = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const formatDateTime = (iso) => {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const safeSlug = (title, fallback) => {
  const base = (title || fallback || 'entry')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return base || 'entry';
};

const downloadFile = (filename, content) => {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const entryToText = (entry) => {
  const heading = entry.title ? entry.title : 'Untitled entry';
  return `${heading}\n${formatDateTime(entry.createdAt)}\n${'-'.repeat(heading.length)}\n\n${entry.body}\n`;
};

const Journal = () => {
  const [entries, setEntries] = useState(loadEntries);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [prompt, setPrompt] = useState(() => PROMPTS[Math.floor(Math.random() * PROMPTS.length)]);
  const editorRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch {
      // Storage full / disabled — fail silently; UI still works for the session.
    }
  }, [entries]);

  const sortedEntries = useMemo(
    () => [...entries].sort((a, b) => (b.createdAt > a.createdAt ? 1 : -1)),
    [entries]
  );

  const resetEditor = () => {
    setTitle('');
    setBody('');
    setEditingId(null);
  };

  const handleSave = (e) => {
    e.preventDefault();
    const trimmedBody = body.trim();
    if (!trimmedBody) return;

    if (editingId) {
      setEntries((prev) =>
        prev.map((en) =>
          en.id === editingId
            ? { ...en, title: title.trim(), body: trimmedBody, updatedAt: new Date().toISOString() }
            : en
        )
      );
    } else {
      const now = new Date().toISOString();
      setEntries((prev) => [
        ...prev,
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          title: title.trim(),
          body: trimmedBody,
          createdAt: now,
          updatedAt: now,
        },
      ]);
    }
    resetEditor();
    setPrompt(PROMPTS[Math.floor(Math.random() * PROMPTS.length)]);
  };

  const handleEdit = (entry) => {
    setEditingId(entry.id);
    setTitle(entry.title || '');
    setBody(entry.body);
    setTimeout(() => {
      editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleDelete = (id) => {
    if (!window.confirm('Delete this entry? This cannot be undone.')) return;
    setEntries((prev) => prev.filter((en) => en.id !== id));
    if (editingId === id) resetEditor();
  };

  const handleDownloadOne = (entry) => {
    const datePart = entry.createdAt.slice(0, 10);
    downloadFile(`journal-${datePart}-${safeSlug(entry.title, datePart)}.txt`, entryToText(entry));
  };

  const handleDownloadAll = () => {
    if (!sortedEntries.length) return;
    const header = `Ishira Journal — exported ${new Date().toLocaleString()}\n${'='.repeat(48)}\n\n`;
    const body = sortedEntries.map(entryToText).join('\n\n---\n\n');
    downloadFile(`ishira-journal-${new Date().toISOString().slice(0, 10)}.txt`, header + body);
  };

  const wordCount = body.trim() ? body.trim().split(/\s+/).length : 0;

  return (
    <>
      <Navbar />
      <div className="journal-page">
        <div className="container">

          <div className="journal-header">
            <span className="section-tag">Worksheets &amp; trackers</span>
            <h1>Journal</h1>
            <p>
              A quiet space to write what's on your mind. Entries stay on this device —
              nothing is sent anywhere. Download any entry, or everything at once, whenever you like.
            </p>
          </div>

          <div className="journal-layout">

            <section className="journal-editor" ref={editorRef}>
              <div className="journal-prompt">
                <span className="prompt-label">Today's prompt</span>
                <p>{prompt}</p>
                <button
                  type="button"
                  className="prompt-shuffle"
                  onClick={() => setPrompt(PROMPTS[Math.floor(Math.random() * PROMPTS.length)])}
                >
                  Try another prompt
                </button>
              </div>

              <form onSubmit={handleSave} className="editor-form">
                <input
                  type="text"
                  className="editor-title"
                  placeholder="Title (optional)"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <textarea
                  className="editor-body"
                  placeholder="Start writing…"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={12}
                />
                <div className="editor-foot">
                  <span className="editor-meta">
                    {wordCount} {wordCount === 1 ? 'word' : 'words'}
                    {editingId && ' · editing existing entry'}
                  </span>
                  <div className="editor-actions">
                    {editingId && (
                      <button type="button" className="btn-secondary" onClick={resetEditor}>
                        Cancel
                      </button>
                    )}
                    <button type="submit" className="btn-primary" disabled={!body.trim()}>
                      {editingId ? 'Update entry' : 'Save entry'}
                    </button>
                  </div>
                </div>
              </form>
            </section>

            <section className="journal-list">
              <div className="list-head">
                <h2>Your entries <span className="entry-count">{sortedEntries.length}</span></h2>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={handleDownloadAll}
                  disabled={!sortedEntries.length}
                >
                  <DownloadIcon size={16} />
                  Download all
                </button>
              </div>

              {sortedEntries.length === 0 ? (
                <div className="empty-state">
                  <p>No entries yet — what you write will appear here.</p>
                </div>
              ) : (
                <ul className="entry-list">
                  {sortedEntries.map((entry) => (
                    <li key={entry.id} className="entry-card">
                      <div className="entry-head">
                        <div>
                          <h3>{entry.title || 'Untitled entry'}</h3>
                          <time dateTime={entry.createdAt}>{formatDateTime(entry.createdAt)}</time>
                        </div>
                        <div className="entry-tools">
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => handleDownloadOne(entry)}
                            aria-label="Download entry"
                            title="Download entry"
                          >
                            <DownloadIcon size={16} />
                          </button>
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => handleEdit(entry)}
                            aria-label="Edit entry"
                            title="Edit entry"
                          >
                            <EditIcon size={16} />
                          </button>
                          <button
                            type="button"
                            className="icon-btn danger"
                            onClick={() => handleDelete(entry.id)}
                            aria-label="Delete entry"
                            title="Delete entry"
                          >
                            <TrashIcon size={16} />
                          </button>
                        </div>
                      </div>
                      <p className="entry-body">{entry.body}</p>
                    </li>
                  ))}
                </ul>
              )}
            </section>

          </div>

          <div className="journal-disclaimer">
            <p>
              <strong>Private to your device.</strong> Entries are stored in your browser's local
              storage and never sent to a server. Clearing your browser data will remove them — use
              <em> Download all </em> regularly if you want a long-term copy.
            </p>
          </div>

        </div>
      </div>
      <Footer />
    </>
  );
};

export default Journal;
